import classnames from 'classnames'
import { useEffect, useMemo } from 'react'

import { ProductCard } from '@src/components/product-card/product-card.component'
import { getInitialSelectedVariant, navigateToPDP } from '@src/components/product-card/product-card.helpers'
import { IProductCard } from '@src/components/product-card/product-card.types'
import { SvdProductCard, mapSvdProduct } from '@src/components/svd-product-card'
import BATCHED_EVENT_LIMIT from '@src/constants/batchedEventLimit'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { TrackingMetaData } from '@src/quince-site-common/src/domains/CustomSection'
import Link from '@src/quince-site-common/src/domains/Link'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { batchedAnalytics } from '@src/quince-site-common/src/utils/batched-analytics'
import { MAX_NUMBER_OF_COLOR_OPTIONS } from '@src/quince-site-common/src/utils/constants'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import { getProductDataForBatchedAnalytics } from '@src/quince-site-common/src/utils/getProductDataForBatchedAnalytics'
import { getProductTagsDataForBatchedAnalytics } from '@src/quince-site-common/src/utils/getProductTagsDataForBatchedAnalytics'
import { FINDING_METHOD_AGGREGATE, setFindingMethod } from '@src/telemetry/findingMethod'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackClickEvent, trackEvent } from '@src/telemetry/track'
import type { ISvdCard } from '@src/types/svd-product-card.types'
import { shouldUseSvdCard } from '@src/utils/should-use-svd-card'

import { RichText, RichTextProps } from '../../molecules'
import { Button, ButtonProps } from '../button'
import { Image, ImageProps } from '../image'
import Swiper from '../swiper/Swiper'

import {
  getContentContainerTrackingMetadata,
  getTrackingLabel,
  TRACKING_METADATA,
} from './imageWithProductCarousel.helpers'
import styles from './imageWithProductCarousel.module.scss'

export interface ImageWithProductCarouselProps {
  bannerImageProps?: ImageProps
  headingRichTextProps?: RichTextProps
  descriptionRichTextProps?: RichTextProps
  productCards?: IProductCard[]
  className?: string
  buttonProps?: ButtonProps
  tracker?: string
  link?: Link
  trackingMetaData?: TrackingMetaData
}

export const ImageWithProductCarousel = ({ className = '', ...props }: ImageWithProductCarouselProps) => {
  const router = useRouter()
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  const wrapClasses = classnames(styles.imageWithProductCarouselWrap, className)

  const productCardMaxWidth = getMaxWidthInPx({
    maxWidthDesktopInRem: styles.productCardWidth ?? '',
    maxWidthMobileInRem: styles.productCardWidthMobile ?? '',
    deviceType: deviceInfo.deviceType,
  })

  const bannerImageMaxWidth = getMaxWidthInPx({
    maxWidthDesktopInRem: styles.leftSectionWidth ?? '',
    maxWidthMobileInRem: styles.leftSectionWidthMobile ?? '',
    deviceType: deviceInfo.deviceType,
  })

  const handleLeftSectionOnClick = () => {
    if (props.link?.navigationPath) {
      trackClickEvent(TELEMETRY_EVENT_NAMES.LINK_CLICK, {
        trackInternal: true,
        context: { ...props.trackingMetaData?.context, value: props.link?.navigationPath },
      })

      setFindingMethod[FINDING_METHOD_AGGREGATE.CAMPAIGN]?.({
        elementName: props.trackingMetaData?.context?.contentContainerName || '',
        elementId: props.trackingMetaData?.context?.trackingId || '',
      })

      router.push(props.link.navigationPath)
    }
  }

  const productViewTracker = useMemo(
    () => props.tracker && `${props.tracker}__product_category_carousel`,
    [props.tracker],
  )

  const contentContainerTrackingMetadata = useMemo(
    () => getContentContainerTrackingMetadata(props.trackingMetaData),
    [props.trackingMetaData],
  )

  useEffect(() => {
    if (productViewTracker) {
      batchedAnalytics.initializeBatchedAnalyticsEvent({
        tracker: productViewTracker,
        eventLimit: BATCHED_EVENT_LIMIT[TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW],
        debouncePeriod: 1000,
      })
      batchedAnalytics.trackBatchedAnalytics({
        id: `internalAnalytics-${productViewTracker}`,
        tracker: productViewTracker,
        callback: ({ events }) => {
          const productList = events.map(event => getProductDataForBatchedAnalytics(event))
          const tagsCount = getProductTagsDataForBatchedAnalytics(productList)
          trackEvent(TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW, {
            productList: productList,
            tagsCount: tagsCount,
            ...props.trackingMetaData?.context,
            ...contentContainerTrackingMetadata,
          })
        },
      })
    }
  }, [contentContainerTrackingMetadata])

  return (
    <div className={wrapClasses}>
      <div
        className={classnames(styles.leftSection, props.link?.navigationPath && styles.leftSection__clickable)}
        onClick={handleLeftSectionOnClick}
      >
        {props.bannerImageProps && (
          <Image
            {...props.bannerImageProps}
            className={classnames(styles.banner, props.bannerImageProps.className)}
            maxWidth={bannerImageMaxWidth}
            // @i18n-ignore
            loading={'lazy'}
          />
        )}
        <div className={styles.content}>
          {props.headingRichTextProps && <RichText {...props.headingRichTextProps} />}
          {props.descriptionRichTextProps && (
            <RichText
              {...props.descriptionRichTextProps}
              className={classnames(styles.descriptioncontent, props.descriptionRichTextProps.className)}
            />
          )}
          {props.buttonProps && (
            <Button
              {...props.buttonProps}
              trackConfig={{
                trackInternal: true,
                context: {
                  ...props.trackingMetaData?.context,
                  findingMethod: FINDING_METHOD_AGGREGATE.CAMPAIGN,
                },
                category: '',
                action: '',
              }}
              className={classnames(props.buttonProps.className, styles.button)}
            ></Button>
          )}
        </div>
      </div>
      <div className={styles.rightSection}>
        {props.productCards && (
          <Swiper
            showDots={false}
            showNavigator={isDesktop}
            numberOfVisibleSlides={isDesktop ? 2 : 1}
            variableWidth
            id={'imageWithProductSlider'}
            isStickyFreeMode={isDesktop}
            // @i18n-ignore
            tracker="image-product-carousel"
            trackingMetaData={props.trackingMetaData}
          >
            {props.productCards.map((productCard: IProductCard, idx: number) => {
              if (shouldUseSvdCard()) {
                // v4 SVD payload: map + render the standalone SVD card (see FlatProductList).
                const svdProduct = mapSvdProduct(productCard.product as unknown as ISvdCard)
                return <SvdProductCard key={svdProduct.productId} className={styles.productCard} product={svdProduct} />
              }
              const _tracker = props.tracker && `${props.tracker}__product_category_carousel__${idx}`
              const initialSelectedVariant = getInitialSelectedVariant({
                cardVariants: productCard.product.cardVariants,
              })

              return (
                <ProductCard
                  className={styles.productCard}
                  key={`${productCard.product.productId}_${initialSelectedVariant?.displayConfig.value}`}
                  productCard={productCard}
                  productViewTracker={productViewTracker ?? ''}
                  tracker={_tracker}
                  maxWidth={productCardMaxWidth}
                  productClickTrackConfig={
                    props.tracker
                      ? {
                          trackInternal: true,
                          trackGA: true,
                          category: TRACKING_METADATA.category,
                          action: TRACKING_METADATA.action,
                          label: getTrackingLabel(props.tracker),
                          context: {
                            product_id: productCard.product.relatedIds.spid,
                            product_color: initialSelectedVariant?.displayConfig.value,
                            col: idx,
                            ...props.trackingMetaData?.context,
                            productPosition: idx,
                            contentContainerType: contentContainerTrackingMetadata.contentContainerType,
                            contentContainerName: contentContainerTrackingMetadata.contentContainerName,
                            contentContainerDisplayName: contentContainerTrackingMetadata.contentContainerDisplayName,
                            findingMethod: FINDING_METHOD_AGGREGATE.CAMPAIGN,
                          },
                        }
                      : undefined
                  }
                  shouldShowMore
                  optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
                  onShowMoreClick={({ selectedVariant }) => {
                    navigateToPDP({
                      slug: productCard.product.slug,
                      tracker: _tracker,
                      selectedOption: selectedVariant?.displayConfig.value,
                      router,
                    })
                  }}
                  showMScroll={false}
                />
              )
            })}
          </Swiper>
        )}
      </div>
    </div>
  )
}

export default ImageWithProductCarousel
